import { useState } from "react";
import { required, useTranslate } from "ra-core";
import { useWatch } from "react-hook-form";
import { Loader2 } from "lucide-react";
import { TextInput } from "@/components/admin/text-input";
import { FileInput } from "@/components/admin/file-input";
import { SelectInput } from "@/components/admin/select-input";
import { DateTimeInput } from "@/components/admin/date-time-input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { Status } from "../misc/Status";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { getCurrentDate } from "./utils";
import { AttachmentField } from "./AttachmentField";
import { foreignKeyMapping } from "./foreignKeyMapping";
import { AutocompleteInput, ReferenceInput } from "@/components/admin";
import { contactOptionText } from "../misc/ContactOption";
import { useIsMobile } from "@/hooks/use-mobile";
import type { AttachmentNote } from "../types";
import { ChannelInput } from "./ChannelInput";
import { attachmentsInline } from "./inlineImages";
import { useNoteImagePaste } from "./useNoteImagePaste";

export const NoteInputs = ({
  showStatus,
  selectReference,
  reference,
  actions,
}: {
  showStatus?: boolean;
  selectReference?: boolean;
  reference?: "contacts" | "deals";
  /** Buttons to put on the Channel line, at its right end. */
  actions?: React.ReactNode;
}) => {
  const isMobile = useIsMobile();
  const imagePaste = useNoteImagePaste();
  const { noteStatuses } = useConfigurationContext();
  const translate = useTranslate();
  const [displayMore, setDisplayMore] = useState(false);

  // We manually define the input labels because the default ones
  // would use the resource from the context, which is either "contact_notes" or "deal_notes",
  // but we want it to be "notes" regardless of the context
  return (
    <div className="space-y-2">
      {/* Above the text, and out with it rather than under "Show options": it
          replaces a header people were typing by hand at the top of the note.
          The form's own buttons, when it passes them, share the line.
          LCCRM-87. */}
      <div className="flex items-start justify-between gap-2">
        <ChannelInput />
        {actions && (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        )}
      </div>
      <TextInput
        source="text"
        label={false}
        multiline
        helperText={false}
        placeholder={translate("resources.notes.inputs.add_note")}
        rows={6}
        onPaste={imagePaste.onPaste}
        validate={imagePaste.validateNotUploading}
      />
      <PastedImages uploading={imagePaste.uploading} />

      {selectReference && reference && (
        <ReferenceInput
          source={foreignKeyMapping[reference]}
          reference={reference}
        >
          <AutocompleteInput
            label={
              reference === "contacts"
                ? "resources.notes.fields.contact_id"
                : "resources.notes.fields.deal_id"
            }
            optionText={
              reference === "contacts" ? contactOptionText : undefined
            }
            helperText={false}
            validate={required()}
            modal
          />
        </ReferenceInput>
      )}

      {!displayMore && !isMobile && (
        <div className="flex justify-end items-center gap-2">
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setDisplayMore(!displayMore);
            }}
            className="text-sm text-muted-foreground underline hover:no-underline p-0 h-auto cursor-pointer"
          >
            {translate("resources.notes.inputs.show_options")}
          </Button>
          <span className="text-sm text-muted-foreground">
            {translate("resources.notes.inputs.options_hint")}
          </span>
        </div>
      )}

      <div
        className={cn(
          "space-y-3 mt-3 overflow-hidden origin-top",
          !isMobile ? "transition-transform ease-in-out duration-300 " : "",
          !displayMore && !isMobile ? "scale-y-0 max-h-0 h-0" : "scale-y-100",
        )}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {showStatus && (
            <SelectInput
              source="status"
              label="resources.notes.fields.status"
              choices={noteStatuses.map((status) => ({
                id: status.value,
                name: status.label,
                value: status.value,
              }))}
              optionText={optionRenderer}
              defaultValue="warm"
              helperText={false}
            />
          )}
          <DateTimeInput
            source="date"
            label="resources.notes.fields.date"
            helperText={false}
            className="text-primary"
            defaultValue={getCurrentDate()}
          />
        </div>
        <FileInput
          source="attachments"
          label="resources.notes.fields.attachments"
          multiple
        >
          <AttachmentField source="src" title="title" target="_blank" />
        </FileInput>
      </div>
    </div>
  );
};

// The text box is plain text, so a pasted image is a markdown line in it and
// nothing more to look at. These thumbnails are the confirmation that the paste
// landed, and that a line still points at a picture. LCCRM-87.
const PastedImages = ({ uploading }: { uploading: boolean }) => {
  const translate = useTranslate();
  const text = useWatch({ name: "text" }) as string | null | undefined;
  const attachments = useWatch({ name: "attachments" }) as
    | AttachmentNote[]
    | null
    | undefined;
  const images = attachmentsInline(text, attachments);
  if (images.length === 0 && !uploading) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {images.map((image) => (
        <img
          key={image.src}
          src={image.src}
          alt={image.title}
          className="h-12 w-auto rounded border border-border object-cover"
        />
      ))}
      {uploading && (
        <span
          role="status"
          className="flex items-center gap-1 text-xs text-muted-foreground"
        >
          <Loader2 className="size-3 animate-spin" />
          {translate("resources.notes.inputs.uploading_image", {
            _: "Uploading image…",
          })}
        </span>
      )}
    </div>
  );
};

const optionRenderer = (choice: any) => {
  return (
    <div>
      <Status status={choice.value} /> {choice.name}
    </div>
  );
};
